import { createAction } from '@ngrx/store';
import { MessageEntity } from '../entities/MessageEntity';
import { MessageHeadEntity } from '../entities/Api/MessageHeadEntity';

export const FetchMessages = createAction('[MESSAGES] fetch', (employee_id: number, include_archived: boolean) => ({
	employee_id,
	include_archived,
}));

export const SetMessages = createAction('[MESSAGES] set messages', (messages: MessageEntity[]) => ({ messages }));

/**
 * Emitted when a getMessages request fails.
 *
 * Callers dispatch FetchMessages and then wait for a terminal action
 * (see MessageCenterService.fetchMessages), and route resolvers block navigation until
 * that observable emits. Without a failure action the observable never emits and the
 * route hangs forever in ResolveStart with no error shown.
 *
 * No reducer handles this action on purpose: a transient failure must not clear the
 * list that is already on screen. It carries an empty array only so the resolvers'
 * `map((res) => res.messages)` keeps working across the action union.
 */
export const FetchMessagesFailed = createAction('[MESSAGES] fetch failed', () => ({
	messages: [] as MessageEntity[],
}));

export const SetUserMessages = createAction('[MESSAGES] set user messages', (messages: MessageEntity[]) => ({
	messages,
}));

export const SendMessage = createAction(
	'[MESSAGES] send message',
	(message: string, receiver_id: number, personal_office_for: number, parent: MessageEntity) => ({
		message,
		receiver_id,
		personal_office_for,
		parent,
	})
);

export const ArchiveMessage = createAction(
	'[MESSAGES] archive message',
	(id: string, personal_office_for: number, archive: boolean, remove: boolean) => ({
		id,
		personal_office_for,
		archive,
		remove,
	})
);

/**
 * Marks the given messages of one conversation as read on the server.
 *
 * The ids are picked by the caller because only it knows which conversation is on screen.
 * Note the server does not fill the per-message `message_read` field in a way the client can
 * rely on — the authoritative unread count is `unread_messages` from getMessageHead, which is
 * why the effect refreshes the head afterwards.
 */
export const MarkMessagesRead = createAction(
	'[MESSAGES] mark messages read',
	(personal_office_for: number, messageIds: string[]) => ({ personal_office_for, messageIds })
);

/**
 * Puts a single message back to unread, the manual counterpart of MarkMessagesRead.
 */
export const MarkMessageUnread = createAction(
	'[MESSAGES] mark message unread',
	(personal_office_for: number, messageId: string) => ({ personal_office_for, messageId })
);

export const setMessageHeader = createAction('[MESSAGES] set message head', (messageHead: MessageHeadEntity[]) => ({
	messageHead,
}));
